export const articleDetails = {
  "term-vs-whole-life-insurance": {
    takeaway:
      "The right life insurance choice starts with the job you need the coverage to do, how long that responsibility lasts, and what fits your budget with room to breathe.",
    sections: [
      {
        heading: "Start with the purpose, not the product",
        body:
          "Life insurance is easiest to understand when you begin with a simple question: what would your family need if you were no longer there to provide for them? For some families, the answer is income replacement during the working years. For others, it includes a mortgage, final expenses, childcare, education, or a legacy gift.",
        bullets: [
          "Name the people who depend on your income or care.",
          "List the obligations you would want covered.",
          "Decide how long those responsibilities are likely to last.",
        ],
      },
      {
        heading: "How term life insurance works",
        body:
          "Term life insurance provides coverage for a specific period, such as 10, 20, or 30 years. It is often used when a family needs substantial protection during years of highest responsibility, such as while children are young or a mortgage is being paid. Because it is temporary coverage, term can often provide a larger benefit for a lower initial cost.",
      },
      {
        heading: "How whole life insurance works",
        body:
          "Whole life insurance is designed to last for a lifetime when premiums are paid as required. It can also build cash value over time. Families often consider whole life when they want permanent protection, final-expense planning, or a policy that can become part of a longer-term legacy strategy.",
      },
      {
        heading: "A calm way to compare your options",
        body:
          "Alicia helps clients compare term and whole life without pressure. The conversation looks at budget, health, age, family responsibilities, future goals, and whether a combination of policies may fit better than choosing only one approach.",
        bullets: [
          "How much coverage do we need today?",
          "How long do we need that coverage to last?",
          "What premium feels sustainable year after year?",
          "Do we want coverage that may build cash value?",
        ],
      },
    ],
  },
  "five-questions-family-coverage": {
    takeaway:
      "A thoughtful family protection plan begins with honest answers about who depends on you, what they would need, and how long they would need it.",
    sections: [
      {
        heading: "1. Who depends on you?",
        body:
          "Dependence is not only financial. A spouse, child, aging parent, business partner, or even a family member who relies on your unpaid care may be affected if something happens to you. Begin by naming the people whose daily life would change.",
      },
      {
        heading: "2. What would they need first?",
        body:
          "Immediate needs often include funeral costs, mortgage or rent, groceries, childcare, insurance premiums, and time to grieve without making rushed financial decisions. A good plan gives a family breathing room before it asks them to rebuild.",
      },
      {
        heading: "3. What future goals still matter?",
        body:
          "Education, retirement, debt freedom, a family business, or a legacy gift may still be important even after a loss. Protection planning helps those goals remain possible rather than forcing a family to abandon them under pressure.",
      },
      {
        heading: "4. What coverage do you already have?",
        body:
          "Employer coverage can be valuable, but it may not follow you if your employment changes. Review existing policies, beneficiaries, coverage amounts, and any gaps before assuming your family is fully protected.",
      },
      {
        heading: "5. What can you sustain comfortably?",
        body:
          "The best plan is one you can keep. Alicia helps families balance meaningful protection with a premium that respects the household budget, so coverage remains a source of peace rather than stress.",
      },
    ],
  },
  "protection-planning-retirement": {
    takeaway:
      "Retirement planning is not only about building assets. It is also about protecting income, health, independence, and the people who may still depend on you.",
    sections: [
      {
        heading: "Protection gives retirement a stronger foundation",
        body:
          "A retirement plan can be disrupted by illness, death, caregiving responsibilities, or the loss of a spouse’s income. Protection planning helps prepare for those realities so one unexpected event does not unravel years of disciplined saving.",
      },
      {
        heading: "Review income from every direction",
        body:
          "Retirement income may come from Social Security, pensions, retirement accounts, part-time work, or business income. Alicia helps clients look at how those sources interact and where insurance may help protect the plan.",
        bullets: [
          "Which income sources continue for a surviving spouse?",
          "Which expenses are essential, and which are flexible?",
          "How might healthcare needs affect monthly cash flow?",
        ],
      },
      {
        heading: "Do not overlook health-related decisions",
        body:
          "Health coverage and Medicare-related choices can shape retirement confidence. Understanding enrollment windows, premiums, benefits, and gaps can help families avoid preventable surprises.",
      },
      {
        heading: "Keep beneficiaries and documents current",
        body:
          "Retirement accounts and life insurance policies usually pass by beneficiary designation, not by a will. Reviewing those designations after marriage, divorce, births, deaths, or business changes is a simple but meaningful act of care.",
      },
    ],
  },
  "planning-with-purpose-stewardship": {
    takeaway:
      "Stewardship means preparing thoughtfully, providing responsibly, and making financial decisions that reflect both love and wisdom.",
    sections: [
      {
        heading: "Money decisions carry meaning",
        body:
          "Financial planning is not only mathematical. It reflects what we value, who we love, and what we feel called to protect. A stewardship-minded conversation makes room for faith, family, generosity, and responsibility.",
      },
      {
        heading: "Protection is an act of care",
        body:
          "Preparing for illness, death, or business disruption is not pessimistic. It is a practical way to care for the people who would carry those burdens. Insurance can create time, options, and dignity during difficult seasons.",
      },
      {
        heading: "Clarity reduces fear",
        body:
          "Many people avoid planning because it feels overwhelming. Breaking decisions into simple questions can restore peace: What do I have? What could happen? Who would be affected? What step can I take today?",
        bullets: [
          "Write down your most important responsibilities.",
          "Review current coverage and beneficiaries.",
          "Discuss your wishes with the people you trust.",
        ],
      },
      {
        heading: "A plan can reflect your values",
        body:
          "Alicia’s role is to listen first, then help clients choose protection that supports their family, honors their convictions, and respects the resources they have worked hard to build.",
      },
    ],
  },
  "business-continuity-key-person": {
    takeaway:
      "Business protection is ultimately about people: the owner, the family behind the owner, the employees, and the customers who rely on the business continuing well.",
    sections: [
      {
        heading: "Ask the question before life forces it",
        body:
          "If an owner or essential team member could not work tomorrow, who would make decisions, cover payroll, reassure customers, and protect the value of the business? A continuity conversation brings those answers into the open before they are urgently needed.",
      },
      {
        heading: "Key-person protection creates breathing room",
        body:
          "Key-person life insurance can provide funds that help a business navigate the loss of someone whose knowledge, relationships, or leadership would be difficult to replace. The benefit may help cover transition costs, debt obligations, hiring, or temporary revenue disruption.",
      },
      {
        heading: "Coordinate the business and the family",
        body:
          "Many business owners have personal and business responsibilities woven together. A thoughtful review looks at both sides so the business strategy does not unintentionally leave the family exposed, and the family plan does not overlook business debt or ownership questions.",
        bullets: [
          "Who owns the business today?",
          "What debts or leases depend on the owner?",
          "Which employees or partners are essential to operations?",
          "What should happen to the business if the owner cannot continue?",
        ],
      },
      {
        heading: "Build a team of advisors",
        body:
          "Insurance is one part of continuity planning. Alicia can coordinate conversations with your attorney, accountant, or other professional advisors so protection decisions support the larger legal and financial picture.",
      },
    ],
  },
  "beneficiary-conversations-legacy": {
    takeaway:
      "Legacy planning is not only for the wealthy. It is for anyone who wants their care, wishes, and resources to reach the right people with less confusion.",
    sections: [
      {
        heading: "Beneficiaries deserve regular attention",
        body:
          "Life insurance policies and retirement accounts often transfer according to beneficiary designations. If those designations are outdated, the results may not match your current wishes. Review them after major life events and at least every few years.",
      },
      {
        heading: "A will and beneficiary designations work differently",
        body:
          "A will is important, but it may not control an account with a named beneficiary. That is why families benefit from looking at documents together rather than assuming one document covers everything.",
      },
      {
        heading: "Talk before your family has to guess",
        body:
          "A loving conversation today can prevent confusion later. Let trusted family members know where important documents are kept, who your professional advisors are, and what values guided your decisions.",
        bullets: [
          "Gather policy and account information in one secure place.",
          "Confirm primary and contingent beneficiaries.",
          "Share your wishes with the people who may need to act.",
          "Review plans after births, deaths, marriages, divorces, or business changes.",
        ],
      },
      {
        heading: "Legacy is about more than money",
        body:
          "A meaningful legacy may include faith, family stories, charitable intentions, business continuity, and the example you leave behind. Alicia helps clients begin with those deeper questions before choosing financial tools.",
      },
    ],
  },
};
